import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import {
  DATE_DISPLAY_TYPE,
  formatDateText,
} from "../../components/date-display/DateDisplay";
import SvgLoadingAnimation from "../../components/loading";
import InlineLoading from "../../components/loading/InlineLoading";
import Button from "../../components/button/Button";
import Table, { TableColumn } from "../../components/table/Table";
import Select from "../../components/form-item/Select";
import ToolTip from "../../components/tooltip/ToolTip";
import DropdownMenu, {
  DropdownMenuItem,
} from "../../components/dropdown-menu/DropdownMenu";
import UserAvatar from "../../components/avatar/UserAvatar";
import PaymentYearFilter, {
  PaymentYearSelection,
} from "../../components/payment-statement/PaymentYearFilter";
import {
  useGetAdminPaymentStatementsQuery,
  useUpdatePaymentStatementStatusMutation,
  useUpdateCreatorCorporationMutation,
  PaymentStatement,
  PAYMENT_STATUS_CANCELED,
} from "../../app/services/paymentStatements";
import { useAutoSelectPaymentYear } from "../../hooks/useAutoSelectPaymentYear";
import { downloadPaymentStatementPdf } from "../../utils/paymentStatement";
import { formatYen, toYen } from "../../utils/money";

const PAGE_SIZE = 20;

const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
`;

// アカウント管理ページ（Account.tsx）と同じ横スクロール対応ラッパー。
const EmptyCell = styled.td.attrs({
  className: "bodytext-13 u-text-light-gray u-text-center",
})`
  padding: 32px 8px !important;
`;

/** 金額セル（右寄せ） */
const AmountCell = styled.td.attrs({ className: "bodytext-13" })`
  text-align: right;
`;

// ステータスのセレクト値（PATCH はトグル API のため現在値と違うときだけ呼ぶ）
const STATUS_OPTIONS = [
  { value: "waiting", name: "支払い待ち" },
  { value: "paid", name: "振込済み" },
];

const CORPORATION_OPTIONS = [
  { value: "individual", name: "個人" },
  { value: "corporate", name: "法人" },
];

/** 空値は em ダッシュで表示する */
const cellText = (value: string | null | undefined): string => value || "—";

/** 支店名（支店番号）の表示テキスト */
const branchText = (
  branch: string | null | undefined,
  branchNumber: string | null | undefined,
): string => {
  if (!branch && !branchNumber) return "—";
  return branchNumber ? `${branch ?? ""}（${branchNumber}）` : (branch ?? "—");
};

const formatDate = (value?: string | null): string =>
  formatDateText(value, DATE_DISPLAY_TYPE.DATE_ONLY) || "—";

/** 金額（整数円の文字列）の表示テキスト。未取得は em ダッシュ */
const yenText = (value: string | null | undefined): string =>
  value != null ? (formatYen(toYen(value)) ?? "—") : "—";

/**
 * 管理者（ADMIN）向け 振込申請（支払明細書）ページ。
 *
 * 全ユーザーの振込申請を年度フィルター付きの表形式で一覧表示する。
 * - ステータス: セレクトで「支払い待ち ⇔ 振込済み」を切替。
 *   取消済み（運用コマンドによる取消。status_type=5）は振込対象外なので
 *   セレクトを出さず「取消済み」の表示のみ（BE も PATCH を 400 で拒否）
 * - 法人区分: セレクトで「個人 / 法人」をユーザー単位に切替
 *   （未設定時は BE が振込先の口座名義から自動判定）
 * - 振込額: 法人は対価（税込）そのまま、個人は源泉徴収控除後の金額
 * - 銀行口座情報（銀行名・支店（支店番号）・口座種別・口座番号・振込口座名義）を表示
 * - 並び順: 支払い待ちが上、次いで申請日の新しい順（BE 側でソート）
 */
const Payment: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] =
    useState<PaymentYearSelection>(currentYear);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { data, isLoading, isFetching, error } =
    useGetAdminPaymentStatementsQuery({
      year: selectedYear,
      limit,
      offset: 0,
    });

  const [updateStatus] = useUpdatePaymentStatementStatusMutation();
  const [updateCorporation] = useUpdateCreatorCorporationMutation();

  const availableYears = data?.data?.available_years ?? [];
  useAutoSelectPaymentYear(availableYears, selectedYear, setSelectedYear);

  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }

  const role: string | undefined = currentUser?.data?.role;
  if (role !== "ADMIN") {
    return <Navigate to="/projects" replace />;
  }

  const statements = data?.data?.payment_requests ?? [];
  const hasNext = data?.data?.has_next ?? false;

  const handleYearSelect = (year: PaymentYearSelection) => {
    setSelectedYear(year);
    setLimit(PAGE_SIZE);
  };

  const handleStatusChange = async (
    statement: PaymentStatement,
    nextValue: string,
  ) => {
    const currentValue = statement.is_paid ? "paid" : "waiting";
    if (nextValue === currentValue) return;
    try {
      await updateStatus({ id: statement.id }).unwrap();
      toast.success(
        nextValue === "paid" ? "振込済みにしました" : "支払い待ちに戻しました",
      );
    } catch {
      toast.error("ステータスの更新に失敗しました");
    }
  };

  const handleCorporationChange = async (
    statement: PaymentStatement,
    nextValue: string,
  ) => {
    const creator = statement.creator;
    if (!creator) return;
    const currentValue = creator.is_corporation ? "corporate" : "individual";
    if (nextValue === currentValue) return;
    try {
      await updateCorporation({
        userId: creator.id,
        isCorporation: nextValue === "corporate",
      }).unwrap();
      toast.success(
        `${creator.display_name} を${nextValue === "corporate" ? "法人" : "個人"}に変更しました`,
      );
    } catch {
      toast.error("法人区分の更新に失敗しました");
    }
  };

  const columns: TableColumn[] = [
    { key: "status", header: "ステータス", width: 116, minWidth: 104 },
    { key: "creator", header: "ユーザー名", minWidth: 140 },
    { key: "application_date", header: "申請日", width: 104, minWidth: 96 },
    { key: "amount", header: "対価（税込）", width: 110, minWidth: 96 },
    { key: "corporation", header: "法人区分", width: 88, minWidth: 80 },
    { key: "transfer_amount", header: "振込額", width: 110, minWidth: 96 },
    { key: "bank", header: "銀行名", minWidth: 110 },
    { key: "bank_branch", header: "支店", minWidth: 110 },
    { key: "account_type", header: "口座種別", width: 88, minWidth: 80 },
    { key: "account_number", header: "口座番号", width: 100, minWidth: 88 },
    { key: "account_name", header: "振込口座", minWidth: 120 },
    { key: "menu", header: "", width: 48, minWidth: 48, resizable: false },
  ];

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            <InlineLoading visible />
          </EmptyCell>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            振込申請の取得に失敗しました。
          </EmptyCell>
        </tr>
      );
    }
    if (statements.length === 0) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>振込申請はありません</EmptyCell>
        </tr>
      );
    }
    return statements.map((statement) => {
      const creator = statement.creator;
      const hasPdf = Boolean(statement.pdf_url);
      const isCancelled = statement.status_type === PAYMENT_STATUS_CANCELED;
      const menuItems: DropdownMenuItem[] = [
        {
          label: "ダウンロード",
          onClick: () => {
            if (hasPdf) {
              downloadPaymentStatementPdf(statement, {
                showMissingToast: true,
              });
            }
          },
          icon: "download",
          disabled: !hasPdf,
        },
      ];
      return (
        // 振込済み・取消済みの行は全体を薄いグレーに沈める
        <tr
          key={statement.id}
          className={
            statement.is_paid || isCancelled
              ? "table-row-dimmed u-text-light-gray"
              : undefined
          }
        >
          {/* ステータス（ポップオーバーをクリップしないよう overflow を解除） */}
          <td className="table-cell-visible">
            {isCancelled ? (
              // 取消済みは振込対象外: 誤って振込・ステータス変更しないよう
              // セレクトを出さず表示のみにする
              <span className="bodytext-13">取消済み</span>
            ) : (
              <Select
                name={`payment-status-${statement.id}`}
                size="small"
                value={statement.is_paid ? "paid" : "waiting"}
                options={STATUS_OPTIONS}
                onChange={(e) => handleStatusChange(statement, e.target.value)}
              />
            )}
          </td>
          {/* table-cell-visible: アバターの影をセルの overflow から解放する */}
          <td className="table-cell-visible">
            <div className="u-row u-gap8 u-items-center">
              <UserAvatar
                userId={creator?.id}
                avatar={creator?.avatar_url}
                displayName={creator?.display_name}
                size={24}
              />
              <span className="bodytext-13">
                {cellText(creator?.display_name)}
              </span>
            </div>
          </td>
          <td className="bodytext-13 u-text-light-gray">
            {formatDate(statement.application_date)}
          </td>
          <AmountCell>{yenText(statement.amount)}</AmountCell>
          {/* 法人区分（ポップオーバーをクリップしないよう overflow を解除） */}
          <td className="table-cell-visible">
            {creator ? (
              <Select
                name={`payment-corporation-${statement.id}`}
                size="small"
                value={creator.is_corporation ? "corporate" : "individual"}
                options={CORPORATION_OPTIONS}
                onChange={(e) =>
                  handleCorporationChange(statement, e.target.value)
                }
              />
            ) : (
              <span className="bodytext-13 u-text-light-gray">—</span>
            )}
          </td>
          <AmountCell>{yenText(statement.transfer_amount)}</AmountCell>
          <ToolTip description={creator?.bank ?? ""} truncatedOnly>
            <td className="bodytext-13">{cellText(creator?.bank)}</td>
          </ToolTip>
          <ToolTip
            description={
              branchText(creator?.bank_branch, creator?.bank_branch_number) ||
              ""
            }
            truncatedOnly
          >
            <td className="bodytext-13">
              {branchText(creator?.bank_branch, creator?.bank_branch_number)}
            </td>
          </ToolTip>
          <td className="bodytext-13">
            {cellText(creator?.account_type_display)}
          </td>
          <td className="bodytext-13">{cellText(creator?.account_number)}</td>
          <ToolTip description={creator?.account_name ?? ""} truncatedOnly>
            <td className="bodytext-13">{cellText(creator?.account_name)}</td>
          </ToolTip>
          <td style={{ textAlign: "center" }}>
            <DropdownMenu items={menuItems} />
          </td>
        </tr>
      );
    });
  };

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="支払明細書" />
        <HelpHint description={"全ユーザーの振込申請の管理・ダウンロードを行います。振込額は法人は対価のまま、個人は源泉徴収控除後の金額です。"} />
      </div>
      <PaymentYearFilter
        years={availableYears}
        selected={selectedYear}
        onSelect={handleYearSelect}
      />
      <Table columns={columns} minWidth={1240}>
        {renderRows()}
      </Table>
      {hasNext && (
        <div className="u-row-center u-ptb16">
          <Button
            variant="text"
            onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
            disabled={isFetching}
          >
            もっと見る
          </Button>
        </div>
      )}
    </PageWrapper>
  );
};

export default Payment;
